/**
 * Stock Report Table — v2 (DataGrid migration POC)
 *
 * Duplicate of `StockReportTable` that uses `@bill/ui/DataGrid` instead of
 * the DevExpress-backed `BStockReportGrid`. Demonstrates end-to-end parity :
 * multi-level grouping, header bands, subtotal / grand-total with live
 * SUBTOTAL formulas, calculated columns expressed as true Excel formulas,
 * currency-aware export, structured file name, default multi-level sort,
 * virtualization and scroll-internal sticky header + footer.
 *
 * Uses the `@bill/ui` column presets (`moneyColumn`, `numberColumn`,
 * `textColumn`) so each column stays a one-liner and the `dataType`
 * rules drive BOTH the on-screen renderer AND the Excel `numFmt` — no
 * more duplicated formatting logic.
 */

import { useMemo } from 'react';
import {
  DataGrid,
  moneyColumn,
  numberColumn,
  textColumn,
  type DataGridBand,
  type DataGridColumn,
  type DataGridDefaultSortKey,
  type DataGridSummaryItem,
  type GroupLevel,
} from '@bill/ui';
import { useBrandContext, useCurrency } from '@/hooks';
import { transformForDataGrid, getCategoryLevelLabel } from '../utils/transforms';
import type { FlatStockReportRow } from '../utils/transforms';
import type { StockReportRow } from '@bstock/shared';
import { MarketTypeLabels } from '@bstock/shared';

interface StockReportTableV2Props {
  rows: StockReportRow[];
  isLoading: boolean;
  showFinancials?: boolean;
  dateFrom?: string;
  dateTo?: string;
}

/**
 * On-screen rows consume both the flat category levels (for grouping) and
 * the pre-converted `pmpEur` / valuation fields so the DataGrid renderer
 * never sees centimes — it reads everything in natural units (EUR).
 */
interface FlatStockRow extends FlatStockReportRow {
  marketTypeLabel: string;
  pmpEur: number;
  stockInitialValueEur: number;
  stockFinalValueEur: number;
  varianceValueEur: number;
}

export function StockReportTableV2({
  rows,
  isLoading,
  showFinancials = false,
  dateFrom,
  dateTo,
}: StockReportTableV2Props) {
  const currency = useCurrency();
  const { brand } = useBrandContext();

  // Flatten the category path (categoryLevel0..N) and pre-compute the
  // main-unit currency fields so every downstream consumer (renderer,
  // export, Excel formulas) works in EUR not centimes.
  const { data, maxDepth } = useMemo(() => transformForDataGrid(rows), [rows]);

  const gridData = useMemo<FlatStockRow[]>(
    () =>
      data.map((row) => {
        const pmpEur = row.pmpCentimes / 100;
        const flat = {
          ...row,
          marketTypeLabel:
            MarketTypeLabels[row.marketType as keyof typeof MarketTypeLabels] ??
            row.marketType,
          pmpEur,
          stockInitialValueEur: row.stockInitial * pmpEur,
          stockFinalValueEur: row.stockFinal * pmpEur,
          varianceValueEur: row.variance * pmpEur,
        } as FlatStockRow;
        // Guarantee a non-empty label for every category level so the
        // grouping engine never produces "undefined" buckets.
        for (let i = 0; i < maxDepth; i++) {
          const key = `categoryLevel${i}` as const;
          if (!flat[key]) flat[key] = i === 0 ? '(Non classé)' : '';
        }
        return flat;
      }),
    [data, maxDepth]
  );

  // ---------- Columns ------------------------------------------------------
  //
  // Columns built with presets : `textColumn` for pure strings, `numberColumn`
  // for quantities (right-aligned, `#,##0.00` in Excel), `moneyColumn` for
  // valuations. Calculated fields carry an `exportFormula` so Excel sees a
  // LIVE formula instead of a frozen number — users can tweak PMP and the
  // valuations recalc automatically.
  //
  const columns = useMemo<DataGridColumn<FlatStockRow>[]>(() => {
    const cols: DataGridColumn<FlatStockRow>[] = [
      textColumn<FlatStockRow>({
        id: 'productName',
        header: 'Produit',
        accessor: 'productName',
        band: 'produits',
        // Wider column, truncate overflow with an ellipsis and expose the
        // full name on hover. Prevents long product labels from forcing
        // line wraps that bloat row height across the whole grid.
        width: '280px',
        maxWidth: '280px',
        ellipsis: true,
      }),
      textColumn<FlatStockRow>({
        id: 'productSku',
        header: 'SKU',
        accessor: 'productSku',
        cellClassName: 'font-mono text-xs',
        band: 'produits',
      }),
      textColumn<FlatStockRow>({
        id: 'unitSymbol',
        header: 'Unité',
        accessor: 'unitSymbol',
        align: 'center',
        band: 'produits',
      }),
    ];

    if (showFinancials) {
      cols.push(
        moneyColumn<FlatStockRow>({
          id: 'pmpEur',
          header: 'PMP',
          accessor: 'pmpEur',
          band: 'produits',
        })
      );
    }

    // ---- Stock section : Initial (+ Val.) | Achats | Transfert In | Transfert Out | Final (+ Val.) ----
    cols.push(
      numberColumn<FlatStockRow>({
        id: 'stockInitial',
        header: 'Initial',
        accessor: 'stockInitial',
        band: 'stock',
      })
    );

    if (showFinancials) {
      cols.push(
        moneyColumn<FlatStockRow>({
          id: 'stockInitialValueEur',
          header: 'Val. Initial',
          accessor: 'stockInitialValueEur',
          band: 'stock',
          // Live Excel formula : Val. Initial = stockInitial × PMP
          exportFormula: ({ col, rowRef }) =>
            `${col('stockInitial')}${rowRef}*${col('pmpEur')}${rowRef}`,
        })
      );
    }

    cols.push(
      numberColumn<FlatStockRow>({
        id: 'receipts',
        header: 'Achats',
        accessor: 'receipts',
        band: 'stock',
      }),
      numberColumn<FlatStockRow>({
        id: 'transfersIn',
        header: 'Transfert In',
        accessor: 'transfersIn',
        band: 'stock',
      }),
      numberColumn<FlatStockRow>({
        id: 'transfersOut',
        header: 'Transfert Out',
        accessor: 'transfersOut',
        band: 'stock',
      }),
      numberColumn<FlatStockRow>({
        id: 'stockFinal',
        header: 'Final',
        accessor: 'stockFinal',
        band: 'stock',
      })
    );

    if (showFinancials) {
      cols.push(
        moneyColumn<FlatStockRow>({
          id: 'stockFinalValueEur',
          header: 'Val. Final',
          accessor: 'stockFinalValueEur',
          band: 'stock',
          exportFormula: ({ col, rowRef }) =>
            `${col('stockFinal')}${rowRef}*${col('pmpEur')}${rowRef}`,
        })
      );
    }

    // ---- Ventes / Pertes ----
    cols.push(
      numberColumn<FlatStockRow>({
        id: 'sales',
        header: 'Ventes',
        accessor: 'sales',
        band: 'ventes',
      }),
      numberColumn<FlatStockRow>({
        id: 'lossesRaw',
        header: 'P. Bruts',
        accessor: 'lossesRaw',
        band: 'pertes',
      }),
      numberColumn<FlatStockRow>({
        id: 'lossesFinished',
        header: 'P. Finis',
        accessor: 'lossesFinished',
        band: 'pertes',
      }),
      numberColumn<FlatStockRow>({
        id: 'lossesExceptional',
        header: 'P. Excep',
        accessor: 'lossesExceptional',
        band: 'pertes',
      }),
      numberColumn<FlatStockRow>({
        id: 'employeeMeals',
        header: 'Repas',
        accessor: 'employeeMeals',
        band: 'pertes',
      })
    );

    // ---- Consommation : calculated columns with live Excel formulas ----
    //
    // Conso Théo. = Ventes + Pertes Bruts + Pertes Finis + Pertes Excep + Repas
    // Conso Réelle = Stock Init + Achats + Transferts In − Transferts Out − Stock Final
    // Écart = Conso Réelle − Conso Théo.
    //
    cols.push(
      numberColumn<FlatStockRow>({
        id: 'consumptionTheoretical',
        header: 'Conso Théo.',
        accessor: 'consumptionTheoretical',
        band: 'consommation',
        exportFormula: ({ col, rowRef }) =>
          `${col('sales')}${rowRef}+${col('lossesRaw')}${rowRef}+${col('lossesFinished')}${rowRef}+${col('lossesExceptional')}${rowRef}+${col('employeeMeals')}${rowRef}`,
      }),
      numberColumn<FlatStockRow>({
        id: 'consumptionReal',
        header: 'Conso Réelle',
        accessor: 'consumptionReal',
        band: 'consommation',
        exportFormula: ({ col, rowRef }) =>
          `${col('stockInitial')}${rowRef}+${col('receipts')}${rowRef}+${col('transfersIn')}${rowRef}-${col('transfersOut')}${rowRef}-${col('stockFinal')}${rowRef}`,
      }),
      numberColumn<FlatStockRow>({
        id: 'variance',
        header: 'Écart',
        accessor: 'variance',
        band: 'ecarts',
        exportFormula: ({ col, rowRef }) =>
          `${col('consumptionReal')}${rowRef}-${col('consumptionTheoretical')}${rowRef}`,
      })
    );

    if (showFinancials) {
      cols.push(
        moneyColumn<FlatStockRow>({
          id: 'varianceValueEur',
          header: 'Val. Écart',
          accessor: 'varianceValueEur',
          band: 'ecarts',
          exportFormula: ({ col, rowRef }) =>
            `${col('variance')}${rowRef}*${col('pmpEur')}${rowRef}`,
        })
      );
    }

    return cols;
  }, [showFinancials]);

  // ---------- Grouping -----------------------------------------------------
  //
  // Dynamic multi-level: Market → Category 0 → … → Category N-1.
  //
  const grouping = useMemo(() => {
    const levels: GroupLevel<FlatStockRow>[] = ['marketTypeLabel'];
    for (let i = 0; i < maxDepth; i++) {
      levels.push(`categoryLevel${i}` as keyof FlatStockRow);
    }
    return {
      groupBy: levels,
      defaultExpanded: 'all' as const,
      ungroupedLabel: '(Non classé)',
      renderGroupHeader: ({ level, key }: { level: number; key: string }) => {
        if (level === 0) return <span className="text-primary-700">{key}</span>;
        const label = getCategoryLevelLabel(level - 1);
        return (
          <span>
            <span className="text-xs text-neutral-500 mr-2">{label}:</span>
            <span>{key}</span>
          </span>
        );
      },
    };
  }, [maxDepth]);

  // ---------- Summary ------------------------------------------------------
  //
  // Summing physical quantities across products of different units (kg + L
  // + pièces) has no business meaning — only monetary valuations can be
  // aggregated. The Excel export replaces these with live SUBTOTAL formulas
  // automatically, so filtering in Excel recalculates them.
  //
  const summary = useMemo<DataGridSummaryItem<FlatStockRow>[]>(() => {
    if (!showFinancials) return [];
    return [
      {
        columnId: 'stockInitialValueEur',
        aggregate: 'sum',
        value: (r) => r.stockInitialValueEur,
      },
      {
        columnId: 'stockFinalValueEur',
        aggregate: 'sum',
        value: (r) => r.stockFinalValueEur,
      },
      {
        columnId: 'varianceValueEur',
        aggregate: 'sum',
        value: (r) => r.varianceValueEur,
      },
    ];
  }, [showFinancials]);

  // Header bands — mirror the DevExpress layout exactly.
  const bands = useMemo<DataGridBand[]>(
    () => [
      { id: 'produits', label: 'Produits' },
      { id: 'stock', label: 'Stock' },
      { id: 'ventes', label: 'Ventes' },
      { id: 'pertes', label: 'Pertes' },
      { id: 'consommation', label: 'Consommation' },
      { id: 'ecarts', label: 'Écarts' },
    ],
    []
  );

  // Default multi-level sort : market → cat principale → cat secondaire → product.
  const defaultSort = useMemo<DataGridDefaultSortKey<FlatStockRow>[]>(
    () => [
      (row) => row.marketTypeLabel,
      (row) => (row.categoryLevel0 as string | undefined) ?? '',
      (row) => (row.categoryLevel1 as string | undefined) ?? '',
      (row) => row.productName,
    ],
    []
  );

  return (
    <DataGrid<FlatStockRow>
      title="Détail par produit"
      data={gridData}
      columns={columns}
      bands={bands}
      rowKey={(row) => row.productId}
      isLoading={isLoading}
      skeletonRows={8}
      grouping={grouping}
      summary={summary}
      defaultSort={defaultSort}
      showGrandTotal
      maxHeight="calc(100vh - 200px)"
      density="comfortable"
      currency={currency}
      exportable={{
        fileName: {
          base: 'rapport_stock',
          brand: brand?.name,
          period: { from: dateFrom, to: dateTo },
          suffixes: ['v2'],
        },
        sheetName: 'Rapport Stock',
      }}
      emptyState={
        <div className="py-8 text-center text-sm text-neutral-500">
          Aucune donnée pour cette période
        </div>
      }
    />
  );
}
